import type { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { ALPHA, MOTION } from "../../../theme/tokens";

/** How long a deep-link wash runs. The caller clears its highlight state on the same clock. */
export const DEEP_LINK_HIGHLIGHT_MS = MOTION.DURATION.ROW_HIGHLIGHT;

const washColor = (theme: Theme): string => alpha(theme.palette.primary.main, ALPHA.BRAND_TINT);

/** The animated half of `deepLinkWashStyle`, for a caller that merges its own reduced-motion block. */
export const deepLinkWashAnimation = (theme: Theme): SystemStyleObject<Theme> => ({
	"@keyframes deepLinkWash": {
		"0%, 65%": { backgroundColor: washColor(theme) },
		"100%": { backgroundColor: "transparent" },
	},
	animation: `deepLinkWash ${DEEP_LINK_HIGHLIGHT_MS}ms ${MOTION.EASE_OUT}`,
});

/** The reduced-motion half of `deepLinkWashStyle`: the wash held still, for the caller to drop on its clock. */
export const deepLinkWashReducedMotion = (theme: Theme): SystemStyleObject<Theme> => ({ animation: "none", backgroundColor: washColor(theme) });

/**
 * The "you landed here" wash for whatever a deep link pointed at, painted as the element's own
 * background colour. For an element with a plain background (a table row).
 *
 * Most of the duration is a HELD wash, not a fade (the `FacetFilterSection` flash's reasoning): a long
 * fade reads as a rendering artifact, a held tint reads as "this one". Reduced motion keeps the answer
 * to "which one?" by holding the wash with no animation; the caller drops it after the same duration.
 */
export const deepLinkWashStyle = (theme: Theme): SystemStyleObject<Theme> => ({
	...deepLinkWashAnimation(theme),
	"@media (prefers-reduced-motion: reduce)": deepLinkWashReducedMotion(theme),
});

/**
 * The same wash as an overlay, for a surface whose own `background` (a gradient, an image) would sit
 * under an animated background colour and hide it. The caller keeps the element `position: relative`
 * at all times (not only while highlighted), so turning the wash on never shifts layout; the overlay
 * inherits its corner radius and never takes pointer events.
 */
export const deepLinkWashOverlayStyle = (theme: Theme): SystemStyleObject<Theme> => ({
	"&::after": {
		content: '""',
		position: "absolute",
		inset: 0,
		borderRadius: "inherit",
		pointerEvents: "none",
		...deepLinkWashStyle(theme),
	},
});
